import { initShaderProgram } from "../../../../SharedGL/Shader";

export class TextureShader {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;
    attribute vec4 aVertexColor;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform highp float uYPos;
    uniform highp vec2 uScale;

    uniform highp float uBandX0;
    uniform highp float uBandX1;

    varying lowp vec4 vColor;
    varying highp vec2 vPos;
    varying highp float vLocalX;

    void main(void) {
      highp vec4 position = vec4( aVertexPosition.x, aVertexPosition.y + uYPos, aVertexPosition.zw);
      gl_Position = uProjectionMatrix * uModelViewMatrix * position;
      vColor = aVertexColor;
      vPos = vec2(aVertexPosition.x - aVertexColor.w, aVertexPosition.y) * uScale;

      highp float w = max(1.0, uBandX1 - uBandX0);
      vLocalX = clamp((aVertexPosition.x - uBandX0) / w, 0.0, 1.0);
    }
  `;

  // Fragment shader program
  // 1 / 80 = 0.0125 multiplying is faster than dividing so we use the magic number
  private readonly fsSource = `
    precision mediump float;
    precision mediump int;

    varying lowp vec4  vColor;
    varying highp vec2 vPos;
    varying highp float vLocalX;

    uniform sampler2D uSampler;

    // master switch (0 = legacy, 1 = gradient mode)
    uniform int   uBlendMode;

    uniform float uBgFloor;

    // gradient uniforms
    uniform float uOverlayAlpha; // e.g. 0.16..0.22
    uniform float uTint;         // e.g. 0.92..0.98 to soften pattern

    uniform vec3  uRed;
    uniform vec3  uYellow;
    uniform vec3  uOrange;

    // which colors exist in this day
    uniform int   uMode;     // 1,2,3,12,13,23,123

    uniform float uSplitMid; // ~0.5 for two-color
    uniform float uSplitL;   // ~0.33 for three-color
    uniform float uSplitR;   // ~0.66 for three-color
    uniform float uBlendWidth;
    uniform float uPatternOffsetY;
    uniform int uUseMask; 
    uniform highp float uBandX0;

    void main(void) {
      float texV = (uBlendMode == 0) ? vPos.y : (vPos.y + uPatternOffsetY);
      vec4 tex = texture2D(uSampler, vec2(vPos.x * 0.0125, texV));

      if (uBlendMode == 0) {
        gl_FragColor = mix(texture2D(uSampler, vec2(vPos.x * 0.0125, vPos.y)), vColor, 0.8);
        return;
      }

      vec3 c;
      float w = uBlendWidth; // width of the soft blend window (0..1 of day)

      if (uMode == 7) {
        // R | Y | O
        if (vLocalX < uSplitL) {
          float t = smoothstep(uSplitL - w, uSplitL + w, vLocalX);
          c = mix(uRed, uYellow, t);
        } else if (vLocalX < uSplitR) {
          float t = smoothstep(uSplitR - w, uSplitR + w, vLocalX);
          c = mix(uYellow, uOrange, t);
        } else {
          c = uOrange;
        }
      } else if (uMode == 3) {      // R | Y
        float t = smoothstep(uSplitMid - w, uSplitMid + w, vLocalX);
        c = mix(uRed, uYellow, t);
      } else if (uMode == 5) {      // R | O
        float t = smoothstep(uSplitMid - w, uSplitMid + w, vLocalX);
        c = mix(uRed, uOrange, t);
      } else if (uMode == 6) {      // Y | O
        float t = smoothstep(uSplitMid - w, uSplitMid + w, vLocalX);
        c = mix(uYellow, uOrange, t);
      } else if (uMode == 1) {
        c = uRed;
      } else if (uMode == 2) {
        c = uYellow;
      } else {
        c = uOrange;
      }

      c *= uTint;

      float mask = (uUseMask == 1) ? tex.a : 1.0;
      float alpha = uOverlayAlpha * mix(uBgFloor, 1.0, mask);

      gl_FragColor = vec4(c, alpha);
    }
  `;

  public shaderProgram: WebGLProgram;
  constructor(gl: WebGLRenderingContext) {
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);
  }
}
